/**
 * Authentication Store
 */
import { writable } from 'svelte/store';
import { supabase } from '../supabaseClient';
import { APP_URL, APP_NAME } from '../api/config';
import type { User } from '@supabase/supabase-js';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  /** True while the user is in a password-recovery session (PASSWORD_RECOVERY event). */
  recoveryMode: boolean;
  /** Authenticator Assurance Level for the current session. */
  aal: { current: string | null; next: string | null };
  /** True when the user has a verified factor but hasn't passed it this session. */
  mfaRequired: boolean;
}

const initialAal = { current: null, next: null };

// Recovery mode is persisted in sessionStorage so it survives the refresh that
// follows clicking the email link, and isn't clobbered by the SIGNED_IN /
// INITIAL_SESSION events (or getSession) that fire when the recovery session is
// established. It is cleared only by completing or cancelling the reset.
const RECOVERY_KEY = 'minertia.passwordRecovery';
const hasStorage = typeof window !== 'undefined' && !!window.sessionStorage;
const isRecoveryActive = () => hasStorage && sessionStorage.getItem(RECOVERY_KEY) === '1';
const setRecoveryFlag = () => hasStorage && sessionStorage.setItem(RECOVERY_KEY, '1');
const clearRecoveryFlag = () => hasStorage && sessionStorage.removeItem(RECOVERY_KEY);

function createAuthStore() {
  // Detect a recovery link before anything else: Supabase puts `type=recovery`
  // in the URL hash. Capturing it here means the flag is set regardless of the
  // order in which the PASSWORD_RECOVERY / INITIAL_SESSION events arrive.
  if (typeof window !== 'undefined' && window.location.hash.includes('type=recovery')) {
    setRecoveryFlag();
  }

  const { subscribe, set, update } = writable<AuthState>({
    user: null,
    loading: true,
    error: null,
    recoveryMode: isRecoveryActive(),
    aal: initialAal,
    mfaRequired: false,
  });

  // Never force recoveryMode false here — derive it from the persisted flag so
  // session events that fire during recovery can't drop the reset screen.
  const setSession = (user: User | null) => {
    update((state) => ({
      ...state,
      user,
      loading: false,
      error: null,
      recoveryMode: isRecoveryActive(),
    }));
  };

  // Read the current AAL and derive whether a second factor is still required.
  const refreshAAL = async () => {
    const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    const current = data?.currentLevel ?? null;
    const next = data?.nextLevel ?? null;
    update((state) => ({
      ...state,
      aal: { current, next },
      mfaRequired: current === 'aal1' && next === 'aal2',
    }));
  };

  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'PASSWORD_RECOVERY') {
      setRecoveryFlag();
      update((state) => ({
        ...state,
        user: session?.user ?? null,
        loading: false,
        recoveryMode: true,
      }));
      return;
    }
    setSession(session?.user ?? null);
    if (event === 'SIGNED_OUT') {
      update((state) => ({ ...state, aal: initialAal, mfaRequired: false }));
    } else if (session) {
      void refreshAAL();
    }
  });

  supabase.auth.getSession().then((result) => {
    setSession(result.data.session?.user ?? null);
    if (result.data.session) {
      void refreshAAL();
    }
  });

  // Surface an expired/used recovery link (Supabase puts it in the URL hash).
  if (typeof window !== 'undefined' && window.location.hash.includes('error_description')) {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const description = params.get('error_description');
    if (description) {
      update((state) => ({ ...state, error: description.replace(/\+/g, ' '), loading: false }));
    }
    history.replaceState(null, '', window.location.pathname);
  }

  return {
    subscribe,
    signInWithGoogle: async () => {
      update((state) => ({ ...state, loading: true, error: null }));
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        },
      });
      if (error) {
        update((state) => ({ ...state, error: error.message, loading: false }));
      }
      return data;
    },
    signInWithApple: async () => {
      update((state) => ({ ...state, loading: true, error: null }));
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: window.location.origin,
        },
      });
      if (error) {
        update((state) => ({ ...state, error: error.message, loading: false }));
      }
      return data;
    },
    signInWithEmail: async (email: string, password: string) => {
      if (!email || !password) {
        update((state) => ({ ...state, error: 'Please enter both email and password.', loading: false }));
        return null;
      }
      update((state) => ({ ...state, loading: true, error: null }));
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        update((state) => ({ ...state, error: error.message, loading: false }));
      }
      return data;
    },
    signUpWithEmail: async (email: string, password: string) => {
      if (!email || !password) {
        update((state) => ({ ...state, error: 'Please enter both email and password.', loading: false }));
        return null;
      }
      if (password.length < 6) {
        update((state) => ({ ...state, error: 'Password must be at least 6 characters.', loading: false }));
        return null;
      }
      update((state) => ({ ...state, loading: true, error: null }));
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      if (error) {
        update((state) => ({ ...state, error: error.message, loading: false }));
      }
      return data;
    },
    signOut: async () => {
      update((state) => ({ ...state, loading: true }));
      const { error } = await supabase.auth.signOut();
      if (error) {
        update((state) => ({ ...state, error: error.message, loading: false }));
      } else {
        clearRecoveryFlag();
        update((state) => ({
          ...state,
          user: null,
          error: null,
          loading: false,
          recoveryMode: false,
          aal: initialAal,
          mfaRequired: false,
        }));
      }
    },

    // --- Password recovery ---------------------------------------------------

    requestPasswordReset: async (email: string): Promise<boolean> => {
      if (!email) {
        update((state) => ({ ...state, error: 'Please enter your email.', loading: false }));
        return false;
      }
      update((state) => ({ ...state, loading: true, error: null }));
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: APP_URL,
      });
      update((state) => ({ ...state, loading: false }));
      if (error) {
        // Surface rate limiting, but don't leak whether the account exists.
        if (error.message?.toLowerCase().includes('rate')) {
          update((state) => ({
            ...state,
            error: 'Please wait a moment before requesting another email.',
          }));
          return false;
        }
      }
      // Always report success to avoid account enumeration.
      return true;
    },

    updatePassword: async (password: string): Promise<boolean> => {
      if (password.length < 6) {
        update((state) => ({ ...state, error: 'Password must be at least 6 characters.', loading: false }));
        return false;
      }
      update((state) => ({ ...state, loading: true, error: null }));
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        update((state) => ({ ...state, error: error.message, loading: false }));
        return false;
      }
      clearRecoveryFlag();
      update((state) => ({ ...state, loading: false, recoveryMode: false }));
      history.replaceState(null, '', window.location.pathname);
      return true;
    },

    exitRecoveryMode: () => {
      clearRecoveryFlag();
      update((state) => ({ ...state, recoveryMode: false, error: null }));
      history.replaceState(null, '', window.location.pathname);
    },

    // --- TOTP MFA ------------------------------------------------------------

    enrollTotp: async (): Promise<
      { factorId: string; qrCode: string; secret: string; uri: string } | null
    > => {
      update((state) => ({ ...state, error: null }));
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        // Shown as the issuer in authenticator apps (e.g. "Minertia: user@example.com").
        // Without this Supabase defaults to the project's Site URL host (e.g. "localhost").
        issuer: APP_NAME,
        friendlyName: `${APP_NAME} (${new Date().toISOString().slice(0, 10)})`,
      });
      if (error || !data) {
        update((state) => ({ ...state, error: error?.message ?? 'Could not start enrollment.' }));
        return null;
      }
      return {
        factorId: data.id,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
        uri: data.totp.uri,
      };
    },

    // Challenge + verify a TOTP factor. Serves both enrollment confirmation and
    // sign-in step-up (the API calls are identical).
    verifyTotp: async (factorId: string, code: string): Promise<boolean> => {
      update((state) => ({ ...state, error: null }));
      const challenge = await supabase.auth.mfa.challenge({ factorId });
      if (challenge.error || !challenge.data) {
        update((state) => ({ ...state, error: challenge.error?.message ?? 'Challenge failed.' }));
        return false;
      }
      const { error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.data.id,
        code,
      });
      if (error) {
        update((state) => ({ ...state, error: 'Incorrect code, please try again.' }));
        return false;
      }
      await refreshAAL();
      return true;
    },

    listFactors: async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error || !data) return [];
      return data.totp ?? [];
    },

    unenrollTotp: async (factorId: string): Promise<boolean> => {
      update((state) => ({ ...state, error: null }));
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) {
        update((state) => ({ ...state, error: error.message }));
        return false;
      }
      await refreshAAL();
      return true;
    },

    refreshAAL,
  };
}

export const auth = createAuthStore();
